import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import fileDownload from 'js-file-download'
import moment from 'moment-timezone'
import isEmail from 'validator/lib/isEmail'
import isURL from 'validator/lib/isURL'
import { HexColorPicker } from 'react-colorful'

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined'
import EditOffOutlinedIcon from '@mui/icons-material/EditOffOutlined'

import FileUploadPopup from '/utils/file-upload-popup'
import ClickToCopy from '/components/click-to-copy'
import currencies from '/utils/currencies.json'
import { hasRequiredVariableValue, isRequiredVariableForTemplate, isRequiredVariableValueForTemplate } from './variable-required'
import { fromNativeDateTimeValue, fromNativeDateValue, isValidDateInput as isValidStudioDateInput, isValidDateTimeInput, isValidTimeInput, toNativeDateTimeValue, toNativeDateValue } from './date-time'
import { AppContext } from '/providers/app-context'

const flattenVariable = (data, prefix = "", rows = []) => {
  if (_.isPlainObject(data)) {
    Object.entries(data).forEach(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key
      flattenVariable(value, path, rows)
    })
    return rows
  }

  rows.push({ var_name: prefix, value: data })
  return rows
}

const coerceValueByVarType = ({ value, varType }) => {
  if (value === null || value === undefined) return value

  if (varType === "boolean") {
    if (typeof value === "boolean") return value
    if (typeof value === "number") return value !== 0
    if (typeof value === "string") {
      const normalised = value.trim().toLowerCase()
      if (normalised === "true") return true
      if (normalised === "false") return false
    }
    return value
  }

  if (varType === "integer") {
    if (typeof value === "number") return Math.trunc(value)
    if (typeof value === "string") {
      const trimmed = value.trim()
      if (trimmed === "") return value
      const parsed = Number.parseInt(trimmed, 10)
      return Number.isNaN(parsed) ? value : parsed
    }
    return value
  }

  if (["number", "money"].includes(varType)) {
    if (typeof value === "number") return value
    if (typeof value === "string") {
      const trimmed = value.trim()
      if (trimmed === "") return value
      const parsed = Number(trimmed)
      return Number.isNaN(parsed) ? value : parsed
    }
    return value
  }

  return value
}

const buildTypedVariable = (rows, catalogMap) => {
  const next = {}

  rows.forEach(row => {
    const varType = catalogMap[row.var_name]?.var_type
    const value = coerceValueByVarType({ value: row.value, varType })
    _.set(next, row.var_name, value)
  })

  return next
}

const getVariableFamily = varName => varName?.split(".")?.[0] ?? ""
const normaliseCustomVariableName = name => {
  const trimmedName = name?.trim() ?? ""
  if (!trimmedName) return ""
  if (trimmedName.startsWith("custom.")) return trimmedName
  return `custom.${trimmedName.replace(/^\.+/, "")}`
}

const getCustomVariableNameError = ({ name, rows }) => {
  const normalisedName = normaliseCustomVariableName(name)
  if (!normalisedName || normalisedName === "custom.") return "Enter a custom variable name."

  const alreadyExists = rows.some(row => (
    row.var_name?.startsWith("custom.")
    && row.var_name.trim() === normalisedName
  ))
  return alreadyExists ? "That custom variable already exists." : ""
}

const getCustomVariableRowsError = rows => {
  const seenNames = new Set()

  for (const row of rows.filter(currentRow => currentRow.var_name?.startsWith("custom."))) {
    const name = row.var_name?.trim() ?? ""
    if (!name || name === "custom.") return "Enter a name for every custom variable."
    if (seenNames.has(name)) return "Custom variable names must be unique."
    seenNames.add(name)
  }

  return ""
}

const getCustomVariableTypeRowsError = ({ rows, catalogMap }) => {
  const missingTypeRow = rows.find(row => (
    row.var_name?.startsWith("custom.")
    && !catalogMap[row.var_name]?.var_type?.trim()
  ))
  return missingTypeRow ? `Select a variable type for ${missingTypeRow.var_name}.` : ""
}

const BARCODE_FORMAT_OPTIONS = ["NONE", "QR_CODE", "AZTEC", "PDF_417", "CODE_128"]
const customVarTypeOptions = [
  "text",
  "www",
  "phone",
  "email",
  "number",
  "integer",
  "date",
  "time",
  "datetime",
  "boolean",
  "money"
]

const getDefaultValueForCatalogItem = item => {
  if (item?.default_value !== undefined && item?.default_value !== null) {
    return coerceDefaultValueByType({
      value: item.default_value,
      varSubType: item?.var_type
    })
  }
  if (item?.var_type === "integer") return 0
  if (item?.var_type === "boolean") return false
  return ""
}

const coerceDefaultValueByType = ({ value, varSubType }) => {
  if (value === null || value === undefined) return value

  if (varSubType === "boolean") {
    if (typeof value === "boolean") return value
    if (typeof value === "number") return value !== 0
    if (typeof value === "string") {
      const normalised = value.trim().toLowerCase()
      if (["true", "1", "yes", "y", "on"].includes(normalised)) return true
      if (["false", "0", "no", "n", "off"].includes(normalised)) return false
    }
    return value
  }

  if (varSubType === "integer") {
    if (typeof value === "number" && Number.isInteger(value)) return value
    const parsed = Number.parseInt(String(value).trim(), 10)
    return Number.isNaN(parsed) ? value : parsed
  }

  if (["number", "money"].includes(varSubType)) {
    if (typeof value === "number") return value
    const parsed = Number.parseFloat(String(value).trim())
    return Number.isNaN(parsed) ? value : parsed
  }

  if (varSubType === "choice") {
    return String(value).trim().toUpperCase()
  }

  return value
}

const formatValueForInput = value => {
  if (_.isPlainObject(value) || Array.isArray(value)) return JSON.stringify(value, null, 2)
  if (value == null) return ""
  return String(value)
}

const getVariablePlaceholder = varSubType => ({
  email: "name@example.com",
  link: "https://example.com",
  www: "https://example.com",
  image: "https://example.com/image.png",
  phone: "+44 20 1234 5678",
  number: "123.45",
  money: "123.45",
  date: "YYYY-MM-DD",
  time: "HH:mm:ss",
  datetime: "YYYY-MM-DDTHH:mm:ss+00:00",
  location: "51.5074,-0.1278",
  boolean: "true or false",
  colour: "#A1B2C3",
  color: "#A1B2C3",
  colours: "#A1B2C3",
  colors: "#A1B2C3",
  choice: "GBP",
  currency_code: "GBP",
  barcode_format: "QR_CODE"
})[varSubType] ?? ""

const isIntegerInput = input => /^-?\d*$/.test(input)
const isDecimalInput = input => /^-?\d*(\.\d*)?$/.test(input)
const isValidCurrencyCodeInput = input => /^[A-Z]{3}$/.test(input ?? "")
  && currencies.some(currency => currency.code === input)
const isColourSubType = varSubType => ["colour", "color", "colours", "colors"].includes(varSubType)
const isNumberSubType = varSubType => ["integer", "number", "money"].includes(varSubType)
const isValidImageUrlInput = input => {
  if (!isURL(input, { protocols: ['http', 'https'], require_protocol: true })) return false

  try {
    const { pathname } = new URL(input)
    return /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(pathname)
  }
  catch {
    return false
  }
}
const isValidColourInput = input => /^#?[0-9a-fA-F]{6}$/.test(input)
const normaliseColourInput = input => /^#[0-9a-fA-F]{6}$/.test(input)
  ? input
  : /^[0-9a-fA-F]{6}$/.test(input)
    ? `#${input}`
    : null
const isValidDateInput = input => isValidStudioDateInput(input) || input === null
const isValidLocationInput = input => {
  if (typeof input !== "string") return false

  const parts = input.split(",").map(part => part.trim())
  if (parts.length !== 2 || parts.some(part => part === "")) return false

  const [latitude, longitude] = parts.map(Number)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false

  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
}

const resolveEditorValueType = meta => {
  if (!meta) return "text"

  if (meta.source_type === "custom" || meta.var_name?.startsWith("custom.")) return meta.var_type ?? "text"

  const supportedTypes = [
    "text", "email", "phone", "www", "link", "image", "integer", "number", "money",
    "date", "time", "datetime", "location", "boolean", "choice", "currency_code",
    "barcode_format", "colour", "color", "colours", "colors"
  ]

  return [meta.var_formatting_sub_type, meta.var_ui_hint, meta.var_type]
    .find(type => supportedTypes.includes(type)) ?? "text"
}

const validateValueInput = ({ input, varSubType, required = false, label = "Value" }) => {
  const value = input ?? ""
  if (!hasRequiredVariableValue(value)) {
    return required
      ? { valid: false, helperText: `${label} is required.` }
      : { valid: true, helperText: "" }
  }

  switch (varSubType) {
    case "email":
      return {
        valid: isEmail(value),
        helperText: isEmail(value) ? "" : "Enter a valid email address."
      }
    case "link":
      return {
        valid: isURL(value, { protocols: ['http', 'https'], require_protocol: true }),
        helperText: isURL(value, { protocols: ['http', 'https'], require_protocol: true }) ? "" : "Enter a valid URL starting with http:// or https://."
      }
    case "image":
      return {
        valid: isValidImageUrlInput(value),
        helperText: isValidImageUrlInput(value) ? "" : "Enter a valid http:// or https:// image URL ending in an image file extension."
      }
    case "number":
    case "money":
      return {
        valid: isDecimalInput(value),
        helperText: isDecimalInput(value) ? "" : "Enter a valid number."
      }
    case "date":
      return {
        valid: isValidDateInput(value),
        helperText: isValidDateInput(value) ? "" : "Use ISO format YYYY-MM-DD and enter a valid date."
      }
    case "time":
      return {
        valid: isValidTimeInput(value),
        helperText: isValidTimeInput(value) ? "" : "Use time format HH:mm:ss and enter a valid time."
      }
    case "datetime":
      return {
        valid: isValidDateTimeInput(value),
        helperText: isValidDateTimeInput(value) ? "" : "Use ISO format YYYY-MM-DDTHH:mm:ss±HH:MM and a valid time."
      }
    case "location":
      return {
        valid: isValidLocationInput(value),
        helperText: isValidLocationInput(value) ? "" : "Use latitude,longitude with valid ranges."
      }
    case "boolean":
      return {
        valid: ["true", "false"].includes(value),
        helperText: ["true", "false"].includes(value) ? "" : "Choose true or false."
      }
    case "colour":
    case "color":
    case "colours":
    case "colors":
      return {
        valid: isValidColourInput(value),
        helperText: isValidColourInput(value) ? "" : "Use a 6-digit hex colour such as #A1B2C3."
      }
    case "choice":
      return {
        valid: isValidCurrencyCodeInput(value),
        helperText: isValidCurrencyCodeInput(value) ? "" : "Use a valid 3-letter ISO currency code such as GBP."
      }
    case "currency_code":
      return {
        valid: isValidCurrencyCodeInput(value),
        helperText: isValidCurrencyCodeInput(value) ? "" : "Use a valid 3-letter ISO currency code such as GBP."
      }
    case "barcode_format":
      return {
        valid: BARCODE_FORMAT_OPTIONS.includes(value),
        helperText: BARCODE_FORMAT_OPTIONS.includes(value) ? "" : `Choose one of: ${BARCODE_FORMAT_OPTIONS.join(", ")}.`
      }
    default:
      return { valid: true, helperText: "" }
  }
}

const parseValueFromInput = ({ input, previousValue, varSubType }) => {
  if (_.isPlainObject(previousValue) || Array.isArray(previousValue)) {
    try {
      return JSON.parse(input)
    }
    catch {
      return input
    }
  }

  if (typeof previousValue === "number" || isNumberSubType(varSubType)) {
    if (input === "") return ""
    const parsed = Number(input)
    return Number.isNaN(parsed) ? input : parsed
  }

  if (typeof previousValue === "boolean" || varSubType === "boolean") {
    return input === "true"
  }

  if (isColourSubType(varSubType)) {
    return normaliseColourInput(input) ?? input
  }

  if (varSubType === "choice") {
    return input?.toString().trim().toUpperCase() ?? input
  }

  if (varSubType === "currency_code" || varSubType === "barcode_format") {
    return input?.toString().trim().toUpperCase() ?? input
  }

  return input
}

const getCustomVariablePayload = catalogItems => {
  return _.sortBy(
    catalogItems
      .filter(item => item.var_name.startsWith("custom."))
      .map(item => ({
        var_name: item.var_name,
        encrypted: Number(item.encrypted) === 1 ? 1 : 0,
        var_type: item?.var_type ?? "text",
        allowable_in_pass_data: Number(item.allowable_in_pass_data) === 1 ? 1 : 0
      })),
    ["var_name"]
  )
}

const mergeUploadedCustomItems = ({ currentRows, uploadedVariables }) => {
  const uploadedRows = flattenVariable(uploadedVariables).filter(row => row.var_name.startsWith("custom."))
  const merged = _.keyBy(currentRows, "var_name")

  uploadedRows.forEach(row => {
    merged[row.var_name] = row
  })

  return Object.values(merged)
}

const parseUploadedCustomVariables = payload => {
  const uploadedVariables = payload?.variables ?? payload
  const uploadedRows = flattenVariable(uploadedVariables).filter(row => row.var_name.startsWith("custom."))
  const uploadedCustomVariables = payload?.customVariables

  if (!uploadedRows.length) {
    return {
      uploadedVariables,
      customVariables: []
    }
  }

  if (!Array.isArray(uploadedCustomVariables)) {
    throw new Error("Uploaded file must include customVariables when custom.* items are present.")
  }

  const customVariableMap = _.keyBy(uploadedCustomVariables, "var_name")

  uploadedRows.forEach(row => {
    const customVariable = customVariableMap[row.var_name]
    if (!customVariable) throw new Error(`Missing custom variable settings for ${row.var_name}.`)

    const encrypted = Number(customVariable.encrypted)
    if (![0, 1].includes(encrypted)) {
      throw new Error(`Custom variable ${row.var_name} must include encrypted as 0 or 1.`)
    }

    if (customVariable?.var_type !== undefined && !customVarTypeOptions.includes(customVariable.var_type)) {
      throw new Error(`Custom variable ${row.var_name} has invalid var_type.`)
    }

    if (customVariable?.allowable_in_pass_data !== undefined && ![0, 1].includes(Number(customVariable.allowable_in_pass_data))) {
      throw new Error(`Custom variable ${row.var_name} has invalid allowable_in_pass_data.`)
    }
  })

  return {
    uploadedVariables,
    customVariables: uploadedRows.map(row => ({
      var_name: row.var_name,
      encrypted: Number(customVariableMap[row.var_name].encrypted),
      var_type: customVariableMap[row.var_name].var_type ?? "text",
      allowable_in_pass_data: customVariableMap[row.var_name].allowable_in_pass_data === undefined
        ? 1
        : Number(customVariableMap[row.var_name].allowable_in_pass_data)
    }))
  }
}

const collectReferencedVarNames = (value, usedVarNames = new Set()) => {
  if (Array.isArray(value)) {
    value.forEach(item => collectReferencedVarNames(item, usedVarNames))
    return usedVarNames
  }

  if (_.isPlainObject(value)) {
    Object.values(value).forEach(item => collectReferencedVarNames(item, usedVarNames))
    return usedVarNames
  }

  if (typeof value === "string") {
    const matches = value.match(/{{([^}]+)}}/g) ?? []
    matches.forEach(match => {
      const varName = match.replace(/^{{|}}$/g, "").trim()
      if (varName) usedVarNames.add(varName)
    })
  }

  return usedVarNames
}

const isCatalogReadonly = ({ item, isManagedPassTemplate = false }) => {
  if (!item) return false
  if (!isManagedPassTemplate) return false
  if (item.readonly_managed !== undefined && item.readonly_managed !== null) return Number(item.readonly_managed) === 1
  // Backward compatibility while catalog data is migrated
  if (item.editable !== undefined && item.editable !== null) return Number(item.editable) !== 1
  return Number(item.readonly) === 1
}

const isValueRequiredForCatalogEntry = item => {
  if (!item) return false
  return Number(item.required_value) === 1
}

const syncCustomCatalogItems = ({ catalogItems, rows, passDataAllowance = {} }) => {
  const customRows = rows.filter(row => row.var_name.startsWith("custom."))
  const customMap = _.keyBy(catalogItems.filter(item => item.var_name.startsWith("custom.")), "var_name")
  const nextCustomItems = customRows.map(row => ({
    var_name: row.var_name,
    encrypted: Number(customMap[row.var_name]?.encrypted) === 1 ? 1 : 0,
    var_type: customMap[row.var_name]?.var_type ?? "text",
    var_ui_hint: customMap[row.var_name]?.var_ui_hint ?? null,
    var_formatting: customMap[row.var_name]?.var_formatting ?? null,
    var_formatting_sub_type: customMap[row.var_name]?.var_formatting_sub_type ?? null,
    required_managed: 0,
    required_standard: 0,
    required_value: 0,
    allowable_in_pass_data: Number(passDataAllowance[row.var_name] ?? customMap[row.var_name]?.allowable_in_pass_data) === 1 ? 1 : 0,
    readonly_managed: 0
  }))

  return [
    ...catalogItems.filter(item => !item.var_name.startsWith("custom.")),
    ...nextCustomItems
  ]
}

const ManageVariable = ({ open, close, variables, setVariables, setCatalogItems, isManagedPassTemplate = false, passGroupId, catalogItems = [], appleModel, appleTemplate, googleClass, googleObject }) => {
  const { client } = useContext(AppContext)
  const canEditReadonly = Number(client?.is_smartix_account) === 1
  const isReadonlyForClient = item => isCatalogReadonly({ item, isManagedPassTemplate }) && !canEditReadonly
  const [rows, setRows] = useState([])
  const [rowInputs, setRowInputs] = useState({})
  const [rowErrors, setRowErrors] = useState({})
  const [fileUploadPopupOpen, setFileUploadPopupOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [deleteDialogVarName, setDeleteDialogVarName] = useState("")
  const [selectedFamily, setSelectedFamily] = useState("")
  const [selectedVarName, setSelectedVarName] = useState("")
  const [customName, setCustomName] = useState("")
  const [customVarType, setCustomVarType] = useState("")
  const [customUiHint, setCustomUiHint] = useState("")
  const [customDefaultValue, setCustomDefaultValue] = useState("")
  const [customEncrypted, setCustomEncrypted] = useState(false)
  const [customAllowableInPassData, setCustomAllowableInPassData] = useState(true)
  const [selectedVarValue, setSelectedVarValue] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [passDataAllowance, setPassDataAllowance] = useState({})

  const catalogMap = useMemo(() => _.keyBy(catalogItems, "var_name"), [catalogItems])
  const referencedVarNames = useMemo(() => {
    const usedVarNames = new Set()
    collectReferencedVarNames(appleModel, usedVarNames)
    collectReferencedVarNames(appleTemplate, usedVarNames)
    collectReferencedVarNames(googleClass, usedVarNames)
    collectReferencedVarNames(googleObject, usedVarNames)
    collectReferencedVarNames(variables, usedVarNames)
    return usedVarNames
  }, [appleModel, appleTemplate, googleClass, googleObject, variables])

  useEffect(() => {
    if (!open) return

    const nextRows = _.sortBy(flattenVariable(_.cloneDeep(variables)), ["var_name"])
    setRows(nextRows)
    setRowInputs(Object.fromEntries(nextRows.map(row => [row.var_name, formatValueForInput(row.value)])))
    setRowErrors({})
    setFileUploadPopupOpen(false)
    setAddDialogOpen(false)
    setDeleteDialogVarName("")
    setSelectedFamily("")
    setSelectedVarName("")
    setCustomName("")
    setCustomVarType("")
    setCustomUiHint("")
    setCustomDefaultValue("")
    setCustomEncrypted(false)
    setCustomAllowableInPassData(true)
    setSelectedVarValue("")
    setErrorMessage("")
    setPassDataAllowance(Object.fromEntries(catalogItems.map(item => [item.var_name, Number(item.allowable_in_pass_data) === 1 ? 1 : 0])))
  }, [open, variables])

  const rowsWithMeta = useMemo(() => {
    return rows.map(row => {
      const meta = catalogMap[row.var_name]
      const readonlyCatalog = isCatalogReadonly({ item: meta, isManagedPassTemplate })
      const isReadonly = readonlyCatalog && !canEditReadonly
      return {
        ...row,
        meta,
        family: getVariableFamily(row.var_name),
        isCustom: row.var_name.startsWith("custom."),
        inUse: referencedVarNames.has(row.var_name),
        encrypted: Number(meta?.encrypted) === 1,
        requiredValue: isValueRequiredForCatalogEntry(meta),
        readonlyCatalog,
        readonly: isReadonly,
        allowableInPassData: Number(passDataAllowance[row.var_name] ?? meta?.allowable_in_pass_data) === 1 && !(isManagedPassTemplate && readonlyCatalog),
        required: isRequiredVariableForTemplate({ catalogEntry: meta, isManagedPassTemplate }),
        valueInput: rowInputs[row.var_name] ?? formatValueForInput(row.value),
        valueError: rowErrors[row.var_name] ?? "",
      }
    })
  }, [canEditReadonly, catalogMap, isManagedPassTemplate, passDataAllowance, referencedVarNames, rowErrors, rowInputs, rows])

  const availableCatalogItems = useMemo(() => {
    const existing = new Set(rows.map(row => row.var_name))
    return catalogItems.filter(item => !existing.has(item.var_name))
  }, [catalogItems, rows])

  const nextVariable = useMemo(() => buildTypedVariable(rows, catalogMap), [rows, catalogMap])
  const nextCatalogItems = useMemo(() => syncCustomCatalogItems({ catalogItems, rows, passDataAllowance }), [catalogItems, passDataAllowance, rows])
  const customVariableRowsError = useMemo(() => getCustomVariableRowsError(rows), [rows])
  const customVariableTypeRowsError = useMemo(() => getCustomVariableTypeRowsError({ rows, catalogMap }), [catalogMap, rows])
  const customVariableError = customVariableRowsError || customVariableTypeRowsError
  const customNameError = useMemo(() => getCustomVariableNameError({ name: customName, rows }), [customName, rows])
  const customTypeError = customVarType ? "" : "Select a variable type."
  const hasAllowanceChanges = useMemo(() => rows.some(row => Number(passDataAllowance[row.var_name] ?? catalogMap[row.var_name]?.allowable_in_pass_data) !== Number(catalogMap[row.var_name]?.allowable_in_pass_data)), [catalogMap, passDataAllowance, rows])
  const hasChanges = useMemo(() => !_.isEqual(nextVariable, variables) || hasAllowanceChanges, [hasAllowanceChanges, variables, nextVariable])

  const availableFamilies = useMemo(() => {
    return [..._.uniq(availableCatalogItems.map(item => getVariableFamily(item.var_name))).sort(), "custom"]
  }, [availableCatalogItems])

  const familyOptions = useMemo(() => {
    return availableCatalogItems.filter(item => getVariableFamily(item.var_name) === selectedFamily)
  }, [availableCatalogItems, selectedFamily])

  useEffect(() => {
    if (!availableFamilies.length) {
      setSelectedFamily("")
      return
    }

    if (!selectedFamily || !availableFamilies.includes(selectedFamily)) {
      setSelectedFamily(availableFamilies[0])
    }
  }, [availableFamilies, selectedFamily])

  useEffect(() => {
    if (!familyOptions.length) {
      setSelectedVarName("")
      return
    }

    if (!selectedVarName || !familyOptions.some(item => item.var_name === selectedVarName)) {
      setSelectedVarName(familyOptions[0].var_name)
    }
  }, [familyOptions, selectedVarName])

  useEffect(() => {
    if (selectedFamily === "custom") return

    const selectedItem = catalogItems.find(item => item.var_name === selectedVarName)
    if (!selectedItem) {
      setSelectedVarValue("")
      return
    }

    const defaultValue = getDefaultValueForCatalogItem(selectedItem)
    setSelectedVarValue(formatValueForInput(defaultValue))
  }, [catalogItems, selectedFamily, selectedVarName])

  const updateRowValue = (varName, nextInput) => {
    const meta = catalogMap[varName]
    const inputValue = meta?.var_type === "choice"
      ? nextInput?.toString().toUpperCase()
      : nextInput

    if (meta?.var_type === "integer" && !isIntegerInput(inputValue)) return
    if (isNumberSubType(meta?.var_type) && meta?.var_type !== "integer" && !isDecimalInput(inputValue)) return
    if (meta?.var_type === "choice" && !/^[A-Z]{0,3}$/.test(inputValue)) return

    setRowInputs(current => ({ ...current, [varName]: inputValue }))

    const validation = validateValueInput({
      input: inputValue,
      varSubType: resolveEditorValueType(meta),
      required: isValueRequiredForCatalogEntry(meta),
      label: varName
    })
    setRowErrors(current => ({ ...current, [varName]: validation.valid ? "" : validation.helperText }))

    if (!validation.valid) return

    setRows(currentRows => currentRows.map(row => {
      if (row.var_name !== varName) return row

      return {
        ...row,
        value: parseValueFromInput({
          input: inputValue,
          previousValue: row.value,
          varSubType: resolveEditorValueType(meta)
        })
      }
    }))
  }

  const resetRowValue = varName => {
    const meta = catalogMap[varName]
    if (isRequiredVariableForTemplate({ catalogEntry: meta, isManagedPassTemplate })) {
      setRowErrors(current => ({ ...current, [varName]: `${varName} is required.` }))
      return
    }

    setRowInputs(current => ({ ...current, [varName]: "" }))
    setRowErrors(current => ({ ...current, [varName]: "" }))
    setRows(currentRows => currentRows.map(row => (
      row.var_name === varName
        ? { ...row, value: null }
        : row
    )))
  }

  const deleteRow = varName => {
    setRowInputs(current => _.omit(current, [varName]))
    setRowErrors(current => _.omit(current, [varName]))
    setRows(currentRows => currentRows.filter(row => row.var_name !== varName))
  }

  const openDeleteDialog = varName => {
    if (isRequiredVariableForTemplate({ catalogEntry: catalogMap[varName], isManagedPassTemplate })) {
      setErrorMessage(`Cannot delete ${varName} because it is marked as required.`)
      return
    }

    if (referencedVarNames.has(varName)) {
      setErrorMessage(`Cannot delete ${varName} because it is currently used in the Apple or Google template.`)
      return
    }

    setDeleteDialogVarName(varName)
    setErrorMessage("")
  }

  const closeDeleteDialog = () => {
    setDeleteDialogVarName("")
  }

  const confirmDeleteRow = () => {
    if (!deleteDialogVarName) return

    deleteRow(deleteDialogVarName)
    closeDeleteDialog()
  }

  const save = () => {
    if (!hasChanges) return

    if (customVariableError) {
      setErrorMessage(customVariableError)
      return
    }

    const missingRequiredItems = catalogItems
      .filter(item => isRequiredVariableValueForTemplate({ catalogEntry: item, isManagedPassTemplate }))
      .filter(item => rows.some(row => row.var_name === item.var_name))
      .filter(item => {
      const row = rows.find(currentRow => currentRow.var_name === item.var_name)
      return !row || !hasRequiredVariableValue(row.value)
    })

    if (missingRequiredItems.length) {
      setErrorMessage(`Required values missing: ${missingRequiredItems.map(item => item.var_name).join(", ")}`)
      return
    }

    setVariables(nextVariable)
    const savedCatalogItems = nextCatalogItems.map(item => ({
      ...item,
      allowable_in_pass_data: Number(passDataAllowance[item.var_name] ?? item.allowable_in_pass_data) === 1 ? 1 : 0
    }))
    if (setCatalogItems && !_.isEqual(savedCatalogItems, catalogItems)) setCatalogItems(savedCatalogItems)
    close()
  }

  const downloadVariables = () => fileDownload(JSON.stringify({
    variables: buildTypedVariable(rows, catalogMap),
    customVariables: getCustomVariablePayload(catalogItems)
  }, null, 2), "data.json")
  
  const openFileUpload = () => setFileUploadPopupOpen(true)
  const closeFileUpload = () => setFileUploadPopupOpen(false)

  const saveUploadedFile = file => {
    try {
      const data = JSON.parse(file)
      const { uploadedVariables, customVariables } = parseUploadedCustomVariables(data)
      const mergedRows = mergeUploadedCustomItems({ currentRows: rows, uploadedVariables })
      setRows(_.sortBy(mergedRows, ["var_name"]))
      setRowInputs(Object.fromEntries(mergedRows.map(row => [row.var_name, formatValueForInput(row.value)])))
      setRowErrors({})
      if (setCatalogItems) {
        setCatalogItems(currentCatalogItems => {
          const customVariableMap = _.keyBy(customVariables, "var_name")
          const nextCustomItems = mergedRows
            .filter(row => row.var_name.startsWith("custom."))
            .map(row => ({
              var_name: row.var_name,
              encrypted: Number(customVariableMap[row.var_name]?.encrypted) === 1 ? 1 : 0,
              var_type: customVariableMap[row.var_name]?.var_type ?? "text",
              var_ui_hint: customVariableMap[row.var_name]?.var_ui_hint ?? null,
              var_formatting: customVariableMap[row.var_name]?.var_formatting ?? null,
              var_formatting_sub_type: customVariableMap[row.var_name]?.var_formatting_sub_type ?? null,
              required_managed: 0,
              required_standard: 0,
              required_value: 0,
              allowable_in_pass_data: Number(customVariableMap[row.var_name]?.allowable_in_pass_data) === 1 ? 1 : 0,
              readonly_managed: 0
            }))

          return _.sortBy([
            ...currentCatalogItems.filter(item => !item.var_name.startsWith("custom.")),
            ...nextCustomItems
          ], ["var_name"])
        })
      }
      setErrorMessage("")
    }
    catch (err) {
      setErrorMessage(err?.message ?? "Uploaded file must be valid JSON and only custom.* items will be merged.")
    }
  }

  const openAddDialog = () => {
    setAddDialogOpen(true)
    setCustomName("")
    setCustomVarType("")
    setCustomUiHint("")
    setCustomDefaultValue("")
    setCustomEncrypted(false)
    setCustomAllowableInPassData(true)
    setErrorMessage("")
  }

  const closeAddDialog = () => {
    setAddDialogOpen(false)
    setCustomUiHint("")
    setErrorMessage("")
  }

  const addItem = () => {
    if (selectedFamily !== "custom") {
      const selectedItem = catalogItems.find(item => item.var_name === selectedVarName)
      if (!selectedItem) {
        setErrorMessage("Choose a catalog item to add.")
        return
      }

      const validation = validateValueInput({
        input: selectedVarValue,
        varSubType: resolveEditorValueType(selectedItem),
        required: isValueRequiredForCatalogEntry(selectedItem),
        label: selectedItem.var_name
      })
      if (!isReadonlyForClient(selectedItem) && !validation.valid) {
        setErrorMessage(validation.helperText)
        return
      }

      const defaultValue = getDefaultValueForCatalogItem(selectedItem)
      const value = !isReadonlyForClient(selectedItem)
        ? parseValueFromInput({
          input: selectedVarValue,
          previousValue: defaultValue,
          varSubType: resolveEditorValueType(selectedItem)
        })
        : defaultValue

      setRows(currentRows => _.sortBy([...currentRows, { var_name: selectedItem.var_name, value }], ["var_name"]))
      closeAddDialog()
      return
    }

    const nextCustomName = normaliseCustomVariableName(customName)
    if (customNameError) {
      setErrorMessage(customNameError)
      return
    }

    if (customTypeError) {
      setErrorMessage(customTypeError)
      return
    }

    const effectiveCustomVarType = customVarType === "textarea" ? "text" : customVarType
    const effectiveCustomUiHint = customVarType === "textarea"
      ? "textarea"
      : (customVarType === "text" ? customUiHint : "")

    const customValidation = validateValueInput({
      input: customDefaultValue,
      varSubType: effectiveCustomVarType,
      required: false,
      label: nextCustomName
    })

    if (!customValidation.valid) {
      setErrorMessage(customValidation.helperText)
      return
    }

    const parsedCustomValue = parseValueFromInput({
      input: customDefaultValue,
      previousValue: getDefaultValueForCatalogItem({ var_type: effectiveCustomVarType }),
      varSubType: effectiveCustomVarType
    })

    setRows(currentRows => _.sortBy([...currentRows, { var_name: nextCustomName, value: parsedCustomValue }], ["var_name"]))
    if (setCatalogItems) {
      setCatalogItems(currentCatalogItems => {
        const withoutCustom = currentCatalogItems.filter(item => item.var_name !== nextCustomName)
        return _.sortBy([
          ...withoutCustom,
          {
            var_name: nextCustomName,
            encrypted: customEncrypted ? 1 : 0,
            var_type: effectiveCustomVarType,
            var_ui_hint: effectiveCustomUiHint || null,
            var_formatting: null,
            var_formatting_sub_type: null,
            required_managed: 0,
            required_standard: 0,
            required_value: 0,
            allowable_in_pass_data: customAllowableInPassData ? 1 : 0,
            readonly_managed: 0
          }
        ], ["var_name"])
      })
    }
    closeAddDialog()
  }

  if (!open) return null

  const hasRowErrors = Object.values(rowErrors).some(Boolean)
  const selectedCatalogItem = selectedFamily !== "custom" ? catalogItems.find(item => item.var_name === selectedVarName) : null
  const selectedValueValidation = validateValueInput({
    input: selectedVarValue,
    varSubType: resolveEditorValueType(selectedCatalogItem),
    required: isValueRequiredForCatalogEntry(selectedCatalogItem),
    label: selectedCatalogItem?.var_name ?? "Value"
  })

  return (
    <>
      <Dialog
        onClose={(event, reason) => {
          if (reason === "backdropClick") return
          close()
        }}
        open={open}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "1200px",
              maxWidth: "1200px",
              height: "800px"
            },
          },
        }}
      >
        <div className="popupFieldEditor flex h-full flex-col">
          <DialogTitle>Manage Dynamic Values</DialogTitle>

          <DialogContent sx={{ display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)', gap: 2, minHeight: 0, flex: '1 1 0', overflow: 'hidden' }}>
            <div className='flex flex-row justify-between items-center gap-5'>
              <div className='flex flex-col'>
                <Typography variant='body2'>Use pre-defined dynamic values where possible. Create custom dynamic values where needed.</Typography>
                <br/>
                <Typography variant='body2'>You can use other dynamkic value names in value to build complex results</Typography>
                <Typography variant='body2'>{'eg. "You have {{loyalty.points.balance}} loyalty points"'}</Typography>
              </div>

              <div className='flex flex-row gap-2'>
                <Button variant="outlined" onClick={openAddDialog}>Add Item</Button>
                <Button variant="outlined" onClick={downloadVariables}>Download</Button>
              </div>
            </div>

            <TableContainer sx={{ minHeight: 0, height: '100%' }}>
              <Table stickyHeader size='small' sx={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '36%' }} />
                  <col style={{ width: '10%' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell>Value name</TableCell>
                    <TableCell align='center'>Encrypted</TableCell>
                    <TableCell align='center'>Read only</TableCell>
                    <TableCell align='center'>Pass data</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell align='right'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsWithMeta.map(row => (
                    <TableRow key={row.var_name}>
                      <TableCell>
                        <ClickToCopy source={row.var_name}>{row.var_name}</ClickToCopy>
                      </TableCell>
                      <TableCell align='center'>{row.encrypted ? <HttpsOutlinedIcon fontSize='small' /> : null}</TableCell>
                      <TableCell align='center'>{row.readonlyCatalog ? <EditOffOutlinedIcon fontSize='small' /> : null}</TableCell>
                      <TableCell align='center'>
                        <Switch
                          checked={row.allowableInPassData}
                          disabled={isManagedPassTemplate && row.readonlyCatalog}
                          onChange={event => setPassDataAllowance(current => ({ ...current, [row.var_name]: event.target.checked ? 1 : 0 }))}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 0, overflow: 'hidden' }}>
                        <SubtypeValueField
                          fullWidth
                          size='small'
                          value={row.valueInput}
                          placeholder={getVariablePlaceholder(resolveEditorValueType(row.meta))}
                          disabled={row.readonly}
                          varSubType={resolveEditorValueType(row.meta)}
                          multiline={row.meta?.var_ui_hint === "textarea"}
                          error={Boolean(row.valueError)}
                          helperText={row.valueError}
                          onChange={nextValue => updateRowValue(row.var_name, nextValue)}
                          onReset={!row.readonly ? () => resetRowValue(row.var_name) : null}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        {row.required ? (
                          <Typography variant='body2' color='text.secondary'>Required</Typography>
                        ) : (
                          <Button color='error' onClick={() => openDeleteDialog(row.var_name)} disabled={row.inUse}>
                            {row.inUse ? "In use" : "Delete"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {!rowsWithMeta.length ? (
                    <TableRow>
                      <TableCell colSpan={6}>No variables configured.</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            {errorMessage ? (
              <div className='h-5 mb-2 mt-2'>
                <Alert severity='warning' sx={{ py: 0 }}>
                  {errorMessage}
                </Alert>
              </div>
            ) : null}

          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2, pt: 0, flex: '0 0 auto', display: 'block' }}>
            <div className='flex flex-col gap-3 w-full' style={{ flex: '0 0 auto' }}>
              <div className='flex flex-row justify-between w-full'>
                <Button variant="outlined" onClick={close}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={hasRowErrors || Boolean(customVariableError) || !hasChanges}>Save</Button>
              </div>
            </div>
          </DialogActions>
        </div>
      </Dialog>

      <Dialog open={addDialogOpen} onClose={closeAddDialog} fullWidth maxWidth='sm'>
        <DialogTitle>Add data variable</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            select
            label='Variable family'
            value={selectedFamily}
            onChange={event => setSelectedFamily(event.target.value)}
            size='small'
            disabled={!availableFamilies.length}
            sx={{ mt: 2 }}
          >
            {availableFamilies.map(family => <MenuItem key={family} value={family}>{family}</MenuItem>)}
          </TextField>

          {selectedFamily !== "custom" ? (
            <>
              <TextField
                select
                label='Variable'
                value={selectedVarName}
                onChange={event => setSelectedVarName(event.target.value)}
                size='small'
                disabled={!familyOptions.length}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                {familyOptions.map(item => (
                  <MenuItem key={item.var_name} value={item.var_name}>
                    {item.var_name}
                  </MenuItem>
                ))}
              </TextField>

              {selectedVarName ? (
                <>
                  {!isReadonlyForClient(catalogMap[selectedVarName]) ? (
                    <SubtypeValueField
                      label='Initial value'
                      value={selectedVarValue}
                      onChange={setSelectedVarValue}
                      size='small'
                      varSubType={resolveEditorValueType(catalogMap[selectedVarName])}
                      multiline={catalogMap[selectedVarName]?.var_ui_hint === "textarea"}
                      error={!selectedValueValidation.valid}
                      helperText={selectedValueValidation.helperText}
                      slotProps={{ inputLabel: { shrink: true } }}
                      placeholder={getVariablePlaceholder(resolveEditorValueType(catalogMap[selectedVarName]))}
                    />
                  ) : (
                    <Alert severity='info'>
                      Read only
                      {catalogMap[selectedVarName]?.encrypted === 1 ? " • Encrypted" : ""}
                      {Number(catalogMap[selectedVarName]?.required_managed) === 1 ? " • Required on managed templates" : ""}
                    </Alert>
                  )}
                </>
              ) : null}
            </>
          ) : (
            <>
              <TextField
                label='Custom variable name'
                value={customName}
                onChange={event => setCustomName(event.target.value)}
                size='small'
                error={Boolean(customNameError)}
                helperText={customNameError || 'The variable will be stored under custom.*'}
                slotProps={{ inputLabel: { shrink: true } }}
                placeholder="Add variable name"
              />

              <TextField
                select
                label='Variable type'
                value={customVarType}
                onChange={event => setCustomVarType(event.target.value)}
                size='small'
                error={Boolean(customTypeError)}
                helperText={customTypeError}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                <MenuItem value="" disabled>Select a variable type</MenuItem>
                {customVarTypeOptions.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>

              {customVarType === "text" ? (
                <TextField
                  select
                  label='Input style'
                  value={customUiHint}
                  onChange={event => setCustomUiHint(event.target.value)}
                  size='small'
                  slotProps={{ inputLabel: { shrink: true } }}
                >
                  <MenuItem value="">Single line text</MenuItem>
                  <MenuItem value="textarea">Multi-line textarea</MenuItem>
                </TextField>
              ) : null}

              <SubtypeValueField
                label='Default value'
                value={customDefaultValue}
                onChange={setCustomDefaultValue}
                size='small'
                varSubType={customVarType === "textarea" ? "text" : customVarType}
                multiline={customVarType === "textarea" || (customVarType === "text" && customUiHint === "textarea")}
                slotProps={{ inputLabel: { shrink: true } }}
                placeholder="NULL"
              />

              <div className='flex flex-row items-center justify-between'>
                <Typography>Encrypt this variable</Typography>
                <Switch checked={customEncrypted} onChange={event => setCustomEncrypted(event.target.checked)} />
              </div>

              <div className='flex flex-row items-center justify-between'>
                <Typography>Allow in pass data</Typography>
                <Switch checked={customAllowableInPassData} onChange={event => setCustomAllowableInPassData(event.target.checked)} />
              </div>

              <Alert severity='info'>
                Any variable containing personally identifiable data, or "magic links", should be encrypted.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <div className='flex flex-row justify-between w-full'>
            <Button variant="outlined" onClick={closeAddDialog}>Cancel</Button>
            <Button
              variant='contained'
              onClick={addItem}
              disabled={selectedFamily === "custom"
                ? Boolean(customNameError || customTypeError)
                : !isReadonlyForClient(selectedCatalogItem) && !selectedValueValidation.valid}
            >
              Add
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteDialogVarName)} onClose={closeDeleteDialog} fullWidth maxWidth='xs'>
        <DialogTitle>Delete variable</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography>
            Delete <strong>{deleteDialogVarName}</strong>? This change will apply when you save the variables.
          </Typography>
        </DialogContent>
        <DialogActions>
          <div className='flex flex-row justify-between w-full'>
            <Button variant="outlined" onClick={closeDeleteDialog}>Cancel</Button>
            <Button color='error' variant='contained' onClick={confirmDeleteRow}>Delete</Button>
          </div>
        </DialogActions>
      </Dialog>

      <FileUploadPopup open={fileUploadPopupOpen} close={closeFileUpload} title="Upload custom variables" accept=".json" save={saveUploadedFile} />
    </>
  )
}

export default ManageVariable

const SubtypeValueField = ({ value, onChange, varSubType, multiline = false, onReset, helperText, error, ...props }) => {
  const dateInputRef = useRef(null)
  const dateTimeInputRef = useRef(null)

  const stringValue = value?.toString?.() ?? ""
  const placeholder = props.placeholder ?? getVariablePlaceholder(varSubType)

  const endAdornment = (varSubType === "date" || varSubType === "datetime" || onReset) ? (
    <InputAdornment position='end'>
      {varSubType === "date" ? (
        <>
          <IconButton edge='end' size='small' onClick={() => dateInputRef.current?.showPicker?.() ?? dateInputRef.current?.click()}>
            <EventOutlinedIcon fontSize='small' />
          </IconButton>
          <input
            ref={dateInputRef}
            type='date'
            value={toNativeDateValue(stringValue)}
            onChange={event => onChange(fromNativeDateValue(event.target.value))}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            tabIndex={-1}
          />
        </>
      ) : null}

      {varSubType === "datetime" ? (
        <>
          <IconButton edge='end' size='small' onClick={() => dateTimeInputRef.current?.showPicker?.() ?? dateTimeInputRef.current?.click()}>
            <ScheduleOutlinedIcon fontSize='small' />
          </IconButton>
          <input
            ref={dateTimeInputRef}
            type='datetime-local'
            step='1'
            value={toNativeDateTimeValue(stringValue)}
            onChange={event => onChange(fromNativeDateTimeValue(event.target.value))}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            tabIndex={-1}
          />
        </>
      ) : null}

      {onReset ? (
        <IconButton edge='end' size='small' aria-label='Reset to null' onClick={onReset}>
          <CloseIcon fontSize='small' />
        </IconButton>
      ) : null}
    </InputAdornment>
  ) : null

  if (varSubType === "boolean") {
    return (
      <div className='flex flex-col gap-2'>
        <TextField
          {...props}
          select
          value={stringValue === "" ? "false" : stringValue.toLowerCase()}
          error={error}
          helperText={helperText}
          onChange={event => onChange(event.target.value)}
        >
          <MenuItem value="false">false</MenuItem>
          <MenuItem value="true">true</MenuItem>
        </TextField>
        {onReset ? (
          <div className='flex justify-end'>
            <IconButton size='small' aria-label='Reset to null' onClick={onReset}>
              <CloseIcon fontSize='small' />
            </IconButton>
          </div>
        ) : null}
      </div>
    )
  }

  if (isColourSubType(varSubType)) {
    const pickerColour = normaliseColourInput(stringValue) ?? "#ffffff"

    return (
      <div className='flex flex-col gap-2'>
        <TextField
          {...props}
          placeholder={placeholder}
          value={stringValue}
          error={error}
          helperText={helperText}
          onChange={event => onChange(event.target.value)}
          InputProps={{
            endAdornment: onReset ? (
              <InputAdornment position='end'>
                <IconButton edge='end' size='small' aria-label='Reset to null' onClick={onReset}>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }}
        />
        <HexColorPicker color={pickerColour} onChange={onChange} />
      </div>
    )
  }

  if (varSubType === "choice") {
    return (
      <TextField
        {...props}
        select
        value={stringValue}
        error={error}
        helperText={helperText}
        onChange={event => onChange(event.target.value)}
      >
        <MenuItem value="">Select currency</MenuItem>
        {currencies.map(currency => (
          <MenuItem key={currency.code} value={currency.code}>
            {currency.code} - {currency.name}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  if (varSubType === "currency_code") {
    return (
      <TextField
        {...props}
        select
        value={stringValue}
        error={error}
        helperText={helperText}
        onChange={event => onChange(event.target.value)}
      >
        <MenuItem value="">Select currency</MenuItem>
        {currencies.map(currency => (
          <MenuItem key={currency.code} value={currency.code}>
            {currency.code} - {currency.name}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  if (varSubType === "barcode_format") {
    return (
      <TextField
        {...props}
        select
        value={stringValue}
        error={error}
        helperText={helperText}
        onChange={event => onChange(event.target.value)}
      >
        {BARCODE_FORMAT_OPTIONS.map(option => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  return (
      <TextField
        {...props}
        placeholder={placeholder}
        value={stringValue}
      type={varSubType === "email" ? "email" : varSubType === "phone" ? "tel" : "text"}
      multiline={multiline}
      minRows={multiline ? 3 : 1}
      error={error}
      helperText={helperText}
      onChange={event => {
        if (varSubType === "integer" && !isIntegerInput(event.target.value)) return
        if (["number", "money"].includes(varSubType) && !isDecimalInput(event.target.value)) return
        if (varSubType === "choice" && !/^[a-zA-Z]{0,3}$/.test(event.target.value)) return
        onChange(["choice", "currency_code", "barcode_format"].includes(varSubType)
          ? event.target.value.toUpperCase()
          : event.target.value)
      }}
      inputProps={{
        inputMode: ["integer", "number", "money"].includes(varSubType) ? "decimal" : undefined,
        maxLength: ["choice", "currency_code"].includes(varSubType) ? 3 : undefined,
        pattern: varSubType === "integer"
          ? "-?[0-9]*"
          : ["number", "money"].includes(varSubType)
            ? "-?[0-9]*[.]?[0-9]*"
            : ["choice", "currency_code"].includes(varSubType)
              ? "[A-Za-z]{3}"
            : undefined
      }}
      InputProps={{
        endAdornment
      }}
      sx={{
        "& .MuiInputBase-input::placeholder, & .MuiInputBase-inputMultiline::placeholder": {
          color: "text.disabled",
          opacity: 1
        }
      }}
    />
  )
}
